import { GetDefensorias } from '@/modules/structure/defensoria/defensoria.service';
import { GetNucleos } from '@/modules/structure/nucleo/nucleo.service';
import { GetPredios } from '@/modules/structure/predio/predio.service';
import { ReadSetorDto } from '@/api.types';
import { SelectOption } from '@/shared/types/selectOption';
import { readForSelect } from '@/modules/people/person.service';
import { useEffect } from 'react';

export interface IOpcaoPredio {
  setores: ReadSetorDto[];
  value: number;
  label: string;
}

interface IOpcao {
  value: number;
  label: string;
}

export function useCarregarDadosIniciais({
  buscar,
  setPessoas,
  setColaboradores,
  setDefensores,
  setEstagiarios,
  setDefensorias,
  setNucleos,
  setPredios,
  setSetoresDisponiveis,
  setLoaded,
}: {
  buscar: () => Promise<void>;
  setPessoas: (value: SelectOption[]) => void;
  setColaboradores: (value: IOpcao[]) => void;
  setDefensores: (value: IOpcao[]) => void;
  setEstagiarios: (value: IOpcao[]) => void;
  setDefensorias: (value: IOpcao[]) => void;
  setNucleos: (value: IOpcao[]) => void;
  setPredios: (value: IOpcaoPredio[]) => void;
  setSetoresDisponiveis: (value: IOpcao[]) => void;
  setLoaded: (value: boolean) => void;
}) {

  useEffect(() => {
    const carregarPessoasParaSelect = () =>
      readForSelect({ skip: 0, take: 9000, comDefensor: true, comEstagiario: true, comColaborador: true, orderBy: 'ASC', })
        .then((result) => {

          const defensoresOptions = result[0]
            .filter((item) => item.defensor)
            .map((item) => ({ value: item.defensor!.id, label: item.nome }));
          setDefensores(defensoresOptions);

          const estagiariosOptions = result[0]
            .filter((item) => item.estagiario)
            .map((item) => ({ value: item.estagiario!.id, label: item.nome }));
          setEstagiarios(estagiariosOptions);

          const colaboradoresOptions = result[0]
            .filter((item) => item.colaborador)
            .map((item) => ({ value: item.colaborador!.id, label: item.nome }));
          setColaboradores(colaboradoresOptions);

          const pessoasOptions = result[0]
            .filter((item) => !item.estagiario)
            .map((item) => ({ value: item.id, label: item.nome }));
          pessoasOptions.unshift({ label: '-', value: '' });
          setPessoas(pessoasOptions);
        });

    const carregarDefensorias = () =>
      GetDefensorias(0, 1000).then((result) => {
        const options = result.objects.map((item) => ({ value: item.id, label: item.nome }));
        setDefensorias(options);
      });

    const carregarPrediosESetores = () =>
      GetPredios(0, 1000).then((result) => {
        const prediosOptions = result.objects.map((item) => ({ value: item.id, label: item.nome, setores: item.setores } as IOpcaoPredio));
        setPredios(prediosOptions);

        // Inicializa setores vazios - serão filtrados quando um prédio for selecionado
        setSetoresDisponiveis([]);
      });

    const carregarNucleos = () =>
      GetNucleos(0, 1000).then((result) => {
        const options = result.objects.map((item) => ({ value: item.id, label: item.nome }));
        setNucleos(options);
      });

    Promise.all([
      carregarPessoasParaSelect(),
      carregarDefensorias(),
      carregarNucleos(),
      carregarPrediosESetores(),
      buscar(),
    ])
      .then(() => setLoaded(true))
      .catch((error) => console.error(error));
  }, [
    buscar,
    setColaboradores,
    setDefensores,
    setDefensorias,
    setEstagiarios,
    setPessoas,
    setLoaded,
    setNucleos,
    setPredios,
    setSetoresDisponiveis,
  ]);
}
